import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Update gallery media in place on variant change.
   * Morphs children keyed by media id so unchanged images are reused (no full remount/flash).
   * No-ops when the variant has no featured media.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const { html, newProduct } = event.detail.data;
    if (!html) return;

    const newMediaGallery = /** @type {MediaGallery | null} */ (html.querySelector('media-gallery'));
    if (!newMediaGallery) return;

    // Combined listings change the product — full replace is required.
    if (newProduct) {
      this.replaceWith(newMediaGallery);
      return;
    }

    const mediaId = event.detail.resource?.featured_media?.id;
    if (mediaId == null) return;

    const id = String(mediaId);
    const firstMediaId = this.querySelector('[data-media-id]')?.getAttribute('data-media-id');
    if (firstMediaId === id) return;

    morph(this, newMediaGallery, {
      ...MORPH_OPTIONS,
      childrenOnly: true,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;

        const slideId = node.getAttribute('slide-id');
        if (slideId) return `slide:${slideId}`;

        const mediaAttr = node.getAttribute('data-media-id');
        if (mediaAttr) return `media:${mediaAttr}`;

        return undefined;
      },
    });

    // Keep the mobile slideshow in sync with the featured media (now first).
    this.slideshow?.select({ id }, undefined, { animate: false });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
